import { produce } from 'immer';
import cloneDeep from 'lodash/cloneDeep';

import { DatasetConfig } from '../../../../models/user-config/DisplayModeConfig';
import {
  downloadFileFromBlob,
  promptDataFromFile,
} from '../../../../services/common/files';
import {
  createDefaultPreset,
  recoverPreset,
} from '../../../../services/dataset/datasetConfigUtils';
import { DatasetGridColumn } from '../../../../services/dataset/view-model/DatasetGridColumn';

const PRESET_FILE_TYPE = 'application/json';

export const copyPreset = (
  config: DatasetConfig,
  name: string
): DatasetConfig => {
  const defaultPresetName = Object.keys(config.preset)[0];
  const sourceName = config.currentPreset || defaultPresetName;

  return produce(config, (draftState: DatasetConfig) => {
    draftState.currentPreset = name;
    draftState.preset[name] = cloneDeep(draftState.preset[sourceName]);
  });
};

const setDefaultPresetByName = (
  config: DatasetConfig,
  columns: DatasetGridColumn[],
  name: string
): DatasetConfig =>
  produce(config, (draftState: DatasetConfig) => {
    draftState.preset[name] = createDefaultPreset(columns);
    draftState.currentPreset = name;
  });

export const createPreset = (
  config: DatasetConfig,
  columns: DatasetGridColumn[],
  name: string
): DatasetConfig => setDefaultPresetByName(config, columns, name);

export const resetPreset = (
  config: DatasetConfig,
  columns: DatasetGridColumn[]
): DatasetConfig => {
  const emptyConfig = {
    columnsOrder: columns.map(column => column.field),
    columnsWidth: {},
    pinned: 0,
    sort: config.sort.filter(e => e.default),
    currentPreset: config.currentPreset,
    preset: config.preset,
  };

  return setDefaultPresetByName(emptyConfig, columns, config.currentPreset);
};

export const deletePreset = (
  config: DatasetConfig,
  presetName: string
): DatasetConfig => {
  const defaultPresetName = Object.keys(config.preset)[0];
  if (presetName === defaultPresetName) {
    return config;
  }

  return produce(config, (draftConfig: DatasetConfig) => {
    delete draftConfig.preset[presetName];
    draftConfig.currentPreset = defaultPresetName;
  });
};

export const exportPreset = (fileName: string, data: unknown): void => {
  const preset = JSON.stringify(data);

  downloadFileFromBlob(fileName, preset, { type: PRESET_FILE_TYPE });
};

export const getVacantName = (names: string[], prefix = `Preset`): string => {
  let index = 0;
  const getName = () => `${prefix} ${index || ''}`.trim();

  while (names.includes(getName())) {
    index++;
  }

  return getName();
};

export const importPreset = async (
  config: DatasetConfig,
  columns: DatasetGridColumn[]
): Promise<DatasetConfig> => {
  const { data, fileName } = await promptDataFromFile(PRESET_FILE_TYPE);
  const loadPreset = JSON.parse(data);
  if (!loadPreset.rules) {
    throw new Error(`Wrong format preset`);
  }

  const availablePresetNames = Object.keys(config.preset);
  const presetName = availablePresetNames.includes(fileName)
    ? getVacantName(availablePresetNames, fileName)
    : fileName;

  return produce(config, (draft: DatasetConfig) => {
    draft.preset = {
      ...config.preset,
      [presetName]: recoverPreset(loadPreset, columns),
    };
  });
};

const existSamePresetName = (
  config: DatasetConfig,
  presetName: string
): boolean => !!config.preset[presetName];

export const renamePreset = (
  config: DatasetConfig,
  name: string
): DatasetConfig => {
  if (existSamePresetName(config, name)) {
    throw new Error(`you can't have a few presets with same name`);
  }

  const { currentPreset: presetName } = config;

  return produce(config, draftState => {
    draftState.currentPreset = name;
    draftState.preset[name] = draftState.preset[presetName];
    delete draftState.preset[presetName];
  });
};
